import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useActiveEvent } from "@/core/active-event"
import {
  eventSettingsQueryOptions,
  normalizeFeatureFlags,
  patchEvent,
  updateFeatureFlags,
  type FeatureCapability,
  type FeatureFlagsPayload,
} from "@/core/settings"
import { ApiError } from "@/core/api"
import { ROSTER_VISIBILITY_OPTIONS, type RosterVisibility } from "@/core/schedule"
import {
  EVERYONE_MENTION_HINT,
  EVERYONE_MENTION_POLICIES,
  NOTIFY_LEVELS,
  type EveryoneMentionPolicy,
  type NotifyLevel,
} from "@/core/channels"
import {
  Card,
  Checkbox,
  Field,
  PrimaryButton,
  Select,
  SectionPage,
  SectionPageFooter,
  Toggle,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/feature-flags")({
  component: FeatureFlagsView,
})

type SurfaceKey =
  | "enableSchedule"
  | "enableTravel"
  | "enableAccommodation"
  | "enableChannels"
  | "enableConcierge"
  | "directMessagesEnabled"

const SURFACES: { key: SurfaceKey; label: string; description: string }[] = [
  { key: "enableSchedule", label: "Schedule", description: "Agenda, sessions and the daily program." },
  { key: "enableTravel", label: "Travel", description: "Flights, transfers and rentals." },
  { key: "enableAccommodation", label: "Accommodation", description: "Lodging assignments and stays." },
  { key: "enableChannels", label: "Channels", description: "Broadcast + group chat channels." },
  { key: "enableConcierge", label: "Concierge", description: "1:1 support tickets." },
  { key: "directMessagesEnabled", label: "Direct messages", description: "Attendee-to-attendee DMs." },
]

const TRAVEL_CAPS: { key: keyof FeatureFlagsPayload; label: string }[] = [
  { key: "flights", label: "Flights" },
  { key: "lodging", label: "Lodging" },
  { key: "ground", label: "Ground transfers" },
  { key: "rentals", label: "Rentals" },
]

type FormState = {
  flags: FeatureFlagsPayload
  surfaces: Record<SurfaceKey, boolean>
  channelDefaults: {
    defaultChannelNotifyLevel: NotifyLevel
    defaultEveryoneMentionPolicy: EveryoneMentionPolicy
    sessionRosterVisibility: RosterVisibility
  }
}

function FeatureFlagsView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const [form, setForm] = useState<FormState | null>(null)
  const [dirty, setDirty] = useState(false)

  // Seed the form from the server row once it loads (and on refetch when clean).
  useEffect(() => {
    if (!q.data || dirty) return
    setForm({
      flags: normalizeFeatureFlags(q.data.featureFlags),
      surfaces: {
        enableSchedule: !!q.data.enableSchedule,
        enableTravel: !!q.data.enableTravel,
        enableAccommodation: !!q.data.enableAccommodation,
        enableChannels: !!q.data.enableChannels,
        enableConcierge: !!q.data.enableConcierge,
        directMessagesEnabled: !!q.data.directMessagesEnabled,
      },
      channelDefaults: {
        defaultChannelNotifyLevel: q.data.defaultChannelNotifyLevel ?? "mentions",
        defaultEveryoneMentionPolicy: q.data.defaultEveryoneMentionPolicy ?? "team",
        sessionRosterVisibility: q.data.sessionRosterVisibility ?? "admin-only",
      },
    })
  }, [q.data, dirty])

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form) throw new Error("Not ready")
      // Channel defaults are plain event columns (CRUD PATCH); flags are an action.
      return Promise.all([
        updateFeatureFlags({
        eventId,
        featureFlags: form.flags,
        enableSchedule: form.surfaces.enableSchedule,
        enableTravel: form.surfaces.enableTravel,
        enableAccommodation: form.surfaces.enableAccommodation,
        enableChannels: form.surfaces.enableChannels,
        enableConcierge: form.surfaces.enableConcierge,
        directMessagesEnabled: form.surfaces.directMessagesEnabled,
        }),
        patchEvent(eventId, form.channelDefaults),
      ])
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Settings saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Failed to save feature flags")
    },
  })

  const setCap = (key: keyof FeatureFlagsPayload, patch: Partial<FeatureCapability>) => {
    setDirty(true)
    setForm((f) =>
      f
        ? { ...f, flags: { ...f.flags, [key]: { ...(f.flags[key] as FeatureCapability), ...patch } } }
        : f,
    )
  }
  const setSurface = (key: SurfaceKey, value: boolean) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, surfaces: { ...f.surfaces, [key]: value } } : f))
  }
  const setChannelDefault = (patch: Partial<FormState["channelDefaults"]>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, channelDefaults: { ...f.channelDefaults, ...patch } } : f))
  }

  return (
    <SectionPage
      title="Feature flags"
      description="Turn event surfaces on or off and control what attendees can self-manage."
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load settings"}
          </p>
        </Card>
      )}

      <Card title="Event surfaces" description="The tabs and modules attendees see in the app.">
        <div className="space-y-2.5">
          {SURFACES.map((s) => (
            <Toggle
              key={s.key}
              label={s.label}
              description={s.description}
              checked={form?.surfaces[s.key] ?? false}
              disabled={!form}
              onChange={(v) => setSurface(s.key, v)}
            />
          ))}
        </div>
      </Card>

      <Card
        title="Channel defaults"
        description="Used for new channels, including session and travel channels created automatically."
      >
        <div className="space-y-4">
          <Field label="Default notifications" hint="Members can change their own.">
            <Select
              value={form?.channelDefaults.defaultChannelNotifyLevel ?? "mentions"}
              disabled={!form}
              onChange={(e) =>
                setChannelDefault({ defaultChannelNotifyLevel: e.target.value as NotifyLevel })
              }
            >
              {NOTIFY_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Who can use @everyone" hint={EVERYONE_MENTION_HINT}>
            <Select
              value={form?.channelDefaults.defaultEveryoneMentionPolicy ?? "team"}
              disabled={!form}
              onChange={(e) =>
                setChannelDefault({
                  defaultEveryoneMentionPolicy: e.target.value as EveryoneMentionPolicy,
                })
              }
            >
              {EVERYONE_MENTION_POLICIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      <Card title="Session rosters" description="Who can see who's going to a session.">
        <Field label="Default for sessions" hint="Each session can override this in its settings.">
          <Select
            value={form?.channelDefaults.sessionRosterVisibility ?? "admin-only"}
            disabled={!form}
            onChange={(e) =>
              setChannelDefault({ sessionRosterVisibility: e.target.value as RosterVisibility })
            }
          >
            {ROSTER_VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </Card>

      <Card
        title="Attendee travel self-service"
        description="What attendees can add or edit on their own travel."
      >
        <div className="space-y-2.5">
          {TRAVEL_CAPS.map((c) => {
            const cap = (form?.flags[c.key] as FeatureCapability | undefined) ?? {
              attendeeCanCreate: false,
              attendeeCanEdit: false,
            }
            return (
              <div
                key={c.key}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-plum-200/60 px-3.5 py-3 dark:border-white/10"
              >
                <span className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                  {c.label}
                </span>
                <div className="flex items-center gap-4">
                  <Checkbox
                    label="Can add"
                    disabled={!form}
                    checked={cap.attendeeCanCreate}
                    onChange={(v) => setCap(c.key, { attendeeCanCreate: v })}
                  />
                  <Checkbox
                    label="Can edit"
                    disabled={!form}
                    checked={cap.attendeeCanEdit}
                    onChange={(v) => setCap(c.key, { attendeeCanEdit: v })}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      <SectionPageFooter>
        <PrimaryButton onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SectionPageFooter>
    </SectionPage>
  )
}
